import type { ReactNode } from 'react';
import { Alert, Flex, Link, Text } from '@backstage/ui';
import {
  APP_REQUIREMENTS_DOC_PATH,
  type UploadFinding,
} from '@internal/plugin-cloudflare-workers-common';
import { ASK_DEVPROD } from './Explain';
import { BRAND_LINK, MONO } from '../ui';

/** Renders `code` spans in finding text, which marks package and file names with backticks. */
function withCode(text: string): ReactNode[] {
  return text.split('`').map((part, i) =>
    i % 2 === 1 ? (
      <code key={i} style={MONO}>
        {part}
      </code>
    ) : (
      part
    ),
  );
}

export interface UploadFindingsProps {
  findings: UploadFinding[];
}

/**
 * Why an upload can't become an app, or what to watch out for, with how to
 * fix each problem and where the rule is explained. Used for the browser
 * check, the server check and later rejections from the validator or agent.
 */
export const UploadFindings = ({ findings }: UploadFindingsProps) => {
  if (findings.length === 0) return null;
  const blocking = findings.some(finding => finding.severity === 'blocking');

  return (
    <Alert
      status={blocking ? 'danger' : 'warning'}
      icon
      title={
        blocking
          ? 'We can’t set up this app yet'
          : 'Your app can be set up, with a few notes'
      }
      description={
        <Flex direction="column" gap="3" mt="1">
          <Flex direction="column" gap="3" role="list">
            {findings.map(finding => (
              <Flex
                key={`${finding.code}-${finding.severity}`}
                direction="column"
                gap="1"
                role="listitem"
              >
                <Text variant="body-medium" weight="bold">
                  {finding.severity === 'warning' ? 'Note: ' : ''}
                  {withCode(finding.problem)}
                </Text>
                {finding.files.length > 0 && (
                  <Text variant="body-small" color="secondary">
                    Found in{' '}
                    {finding.files.map((file, i) => (
                      <span key={file}>
                        {i > 0 && ', '}
                        <code style={MONO}>{file}</code>
                      </span>
                    ))}
                  </Text>
                )}
                <Text variant="body-medium">
                  How to fix: {withCode(finding.howToFix)}
                </Text>
                <Link
                  href={`${APP_REQUIREMENTS_DOC_PATH}#${finding.docsAnchor}`}
                  target="_blank"
                  style={BRAND_LINK}
                >
                  Read the app requirements
                </Link>
              </Flex>
            ))}
          </Flex>
          {blocking && (
            <Text variant="body-small" color="secondary">
              Need something we don’t support yet? Ask in{' '}
              <code style={MONO}>{ASK_DEVPROD}</code>.
            </Text>
          )}
        </Flex>
      }
    />
  );
};
